import { isAtLeastAge } from './ageUtils.js';

const ended = status => ['ended', 'completed', 'inactive', 'cancelled'].includes(status);
const failure = (message, competition = null, retryable = true) => ({phase:'error', message, competition, retryable});

// URL intent is authoritative, even when empty. Never substitute an older invite.
export function invitationKey(search, pending) {
  const params = new URLSearchParams(search);
  return params.has('join') ? params.get('join').trim() : pending || null;
}

export function invitationNextAction(competition, entry) {
  const id = encodeURIComponent(competition.id);
  const query = `?entry=${encodeURIComponent(entry.id)}`;
  if (entry.status === 'active' && !ended(competition.status) && ['knockout', 'season'].includes(competition.type)) {
    return {label:'Select your team', to:`/select-team/${id}${query}`};
  }
  return {label:'View competition', to:`/competition/${id}${query}`};
}

export async function joinInvitation({client, userId, joinKey, onCompetition = () => {}, isCurrent = () => true}) {
  let competition = null;
  const readMembership = async () => {
    const result = await client.from('user_selections').select('id, competition_id, user_id, status')
      .eq('competition_id', competition.id).eq('user_id', userId).order('created_at', {ascending:true}).limit(1).maybeSingle();
    if (result.error) throw result.error;
    const entry = result.data;
    return entry?.id && entry.competition_id === competition.id && entry.user_id === userId ? entry : null;
  };
  const confirmed = entry => ({phase:'confirmed', competition, entry, action:invitationNextAction(competition, entry)});
  try {
    if (!joinKey) return failure('This invitation link is incomplete. Ask the organiser for a new link.', null, false);
    const result = await client.from('competitions')
      .select('id, name, type, status, registration_status, league, season, start_match_day, is_18_plus, requires_approval')
      .eq('registration_key', joinKey).single();
    if (!isCurrent()) return null;
    if (result.error?.code === 'PGRST116' || (!result.error && !result.data)) {
      return failure('This invitation was not found. Ask the organiser for a new link.', null, false);
    }
    if (result.error) throw result.error;
    competition = result.data;
    onCompetition(competition);

    // Read errors never mean “not a member”. A retry checks persistence first.
    const existing = await readMembership();
    if (!isCurrent()) return null;
    if (existing) return confirmed(existing);
    if (competition.registration_status !== 'open' || ended(competition.status)) {
      return failure(`${competition.name} is no longer accepting entries. Contact the organiser.`, competition, false);
    }
    if (competition.is_18_plus) {
      const {data:profile, error} = await client.from('profiles').select('date_of_birth').eq('id', userId).maybeSingle();
      if (error) throw error;
      if (!isCurrent()) return null;
      if (!isAtLeastAge(profile?.date_of_birth, 18)) return failure('This competition is 18+ only. Check your date of birth in your profile or contact the organiser.', competition, false);
    }
    if (!isCurrent()) return null;
    const {error} = await client.from('user_selections').insert({
      competition_id:competition.id, user_id:userId, league:competition.league,
      season:competition.season, match_day:competition.start_match_day,
      status:competition.requires_approval ? 'pending' : 'active',
    }).select('id');
    if (!isCurrent()) return null;
    // Even a duplicate or an empty “success” must be checked against membership.
    const entry = await readMembership();
    if (!isCurrent()) return null;
    if (entry) return confirmed(entry);
    return failure(error ? 'We could not confirm your entry. Try again to check and complete your invitation.'
      : 'Your entry is not confirmed yet. Try again to check before continuing.', competition);
  } catch {
    return isCurrent() ? failure('We could not confirm your entry. Check your connection and try again. Your invitation has been kept.', competition) : null;
  }
}
